import { Anchor, Box, Card, SimpleGrid, Title } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { Fragment, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { Link } from 'react-router'
import { useHasPermissions } from '@/permissions/useHasPermissions'
import { useGetApplicationsInfinite } from '@/queries/applicationQueries'
import { RouterPath, replacePathParams } from '@/router/paths'
import { useSession } from '@/session/SessionContext'
import { Application, ApplicationType } from '@/types/app'
import { CrudAction, Resource } from '@/types/permissions'
import { useFeatureUsage } from '../subscriptions/useFeatureUsage'
import { useUsageLimitModal } from '../subscriptions/useUsageLimitModal'
import { FloatingChatbot } from '../support/FloatingChatbot'
import { VpDemoVideo } from '../support/VpDemoVideo/VpDemoVideo'
import { AddButtonCard } from '../ui-shared/AddButtonCard/AddButtonCard'
import { ErrorWithReload } from '../ui-shared/ErrorWithReload/ErrorWithReload'
import { InfiniteScroll } from '../ui-shared/InfiniteScroll/InfiniteScroll'
import { PlatformAlert } from '../ui-shared/PlatformAlert/PlatformAlert'
import { ApplicationCard } from './ApplicationCard/ApplicationCard'
import { CreateApplicationModal } from './CreateApplicationModal'
import { DeleteApplicationModal } from './DeleteApplicationModal'
import { UpdateApplicationModal } from './UpdateApplicationModal'

// The password expiry banner lives here rather than in HomeLayout because other pages
// under that layout have sticky headers. Rendering it in the layout would cause conflicts
// and layout shifts on those pages.
const PASSWORD_EXPIRY_WARNING_DAYS = 14

export const ApplicationsPage = () => {
  const { user } = useSession()
  const [appToDelete, setAppToDelete] = useState<Application | null>(null)
  const [appToRename, setAppToRename] = useState<Application | null>(null)

  const daysLeft = user?.days_until_password_expires
  const showExpiryWarning =
    !user?.password_expired &&
    typeof daysLeft === 'number' &&
    daysLeft <= PASSWORD_EXPIRY_WARNING_DAYS

  const { usage, refreshFeatureUsage } = useFeatureUsage('number_of_apps')
  const { openUsageLimitModal } = useUsageLimitModal()

  // If usage is undefined, rely on backend validation for app limits.
  const hasRemainingApps = !usage || usage.remaining > 0

  const [
    isCreateModalOpened,
    { open: openCreateModal, close: closeCreateModal }
  ] = useDisclosure(false)

  const [
    isChatbotModalOpened,
    { open: openChatbotModal, close: closeChatbotModal }
  ] = useDisclosure(false)

  const [canCreateApp, canUpdateApp, canDeleteApp] = useHasPermissions(
    Resource.Applications,
    [CrudAction.Create, CrudAction.Update, CrudAction.Delete]
  )

  const { data, isFetching, isError, refetch, fetchNextPage, hasNextPage } =
    useGetApplicationsInfinite()

  const pages = data?.pages

  const handleOnReload = () => {
    void refetch()
  }

  const handleLoadMore = () => {
    void fetchNextPage()
  }

  const handleAppCreateClick = () => {
    if (hasRemainingApps) {
      openCreateModal()
    } else {
      openUsageLimitModal('number_of_apps', {
        maxApps: usage?.limit
      })
    }
  }

  const handleAppDeleted = () => {
    void refetch()
    void refreshFeatureUsage()
    setAppToDelete(null)
  }

  const handleAppUpdated = () => {
    void refetch()
    setAppToRename(null)
  }

  if (isError) {
    return (
      <ErrorWithReload onReload={handleOnReload}>
        <FormattedMessage id="apps.appList.apiError" />
      </ErrorWithReload>
    )
  }

  return (
    <Box pb={120}>
      {showExpiryWarning && (
        <PlatformAlert variant="warning" mb="lg">
          <FormattedMessage
            id="passwordExpiry.warningBanner"
            values={{
              days: daysLeft,
              link: (chunks) => (
                <Anchor
                  size="sm"
                  fw={600}
                  component={Link}
                  to={RouterPath.changePassword}
                >
                  {chunks}
                </Anchor>
              )
            }}
          />
        </PlatformAlert>
      )}

      <Card mb={100} bg="brand-primary.0" py="xl">
        <VpDemoVideo />
      </Card>

      <Title order={2} mb="lg">
        <FormattedMessage id="apps.myApps" />
      </Title>

      <InfiniteScroll
        hasMore={hasNextPage}
        isFetching={isFetching}
        onLoadMore={handleLoadMore}
      >
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 4, xl: 6 }} spacing="xl">
          {canCreateApp && (
            <AddButtonCard
              height={200}
              label={<FormattedMessage id="apps.create" />}
              onClick={handleAppCreateClick}
            />
          )}

          {pages?.map((page, i) => (
            <Fragment key={i}>
              {page.results.map((app) => (
                <ApplicationCard
                  key={app.id}
                  appName={app.name}
                  appDetailsLink={replacePathParams(RouterPath.appDetails, {
                    appId: app.id
                  })}
                  isMultiStage={
                    app.application_type === ApplicationType.MultiStage
                  }
                  showActions={canUpdateApp || canDeleteApp}
                  showEdit={canUpdateApp}
                  showDelete={canDeleteApp}
                  onDelete={() => setAppToDelete(app)}
                  onRename={() => setAppToRename(app)}
                />
              ))}
            </Fragment>
          ))}
        </SimpleGrid>
      </InfiniteScroll>

      <CreateApplicationModal
        opened={isCreateModalOpened}
        onClose={closeCreateModal}
        onOpenChatbot={openChatbotModal}
      />

      {appToRename && (
        <UpdateApplicationModal
          app={appToRename}
          opened={appToRename !== null}
          onClose={() => setAppToRename(null)}
          onAppUpdated={handleAppUpdated}
        />
      )}

      {appToDelete && (
        <DeleteApplicationModal
          app={appToDelete}
          opened={appToDelete !== null}
          onClose={() => setAppToDelete(null)}
          onAppDeleted={handleAppDeleted}
        />
      )}

      <FloatingChatbot
        opened={isChatbotModalOpened}
        onClose={closeChatbotModal}
      />
    </Box>
  )
}
